<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { requiredIf, helpers } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import TestToolFormField from 'dashboard/routes/dashboard/settings/wgptBots/edit/tools/test/TestToolFormField.vue';

const props = defineProps({
  parameterFields: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const formState = ref({});
const formRules = {};

const requiredArray = isRequired =>
  helpers.withParams({ type: 'requiredArray' }, value => {
    if (!isRequired) return true;
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(item => item !== null && item !== '' && item !== undefined)
    );
  });

const validNumber = fieldType =>
  helpers.withParams({ type: 'validNumber' }, value => {
    if (fieldType !== 'integer' && fieldType !== 'float') return true;
    if (value === null || value === '' || value === undefined) return true;
    return !Number.isNaN(Number(value));
  });

const validNumberArray = fieldType =>
  helpers.withParams({ type: 'validNumberArray' }, value => {
    if (fieldType !== 'integer' && fieldType !== 'float') return true;
    if (!Array.isArray(value)) return true;
    return value.every(
      item =>
        item === null ||
        item === '' ||
        item === undefined ||
        !Number.isNaN(Number(item))
    );
  });

props.parameterFields.forEach(field => {
  const defaultValue = field.type === 'boolean' ? false : null;
  if (field.is_array) {
    formState.value[field.name] = field.required ? [defaultValue] : [];
  } else {
    formState.value[field.name] = defaultValue;
  }

  const isRequired = field.type !== 'boolean' && field.required;

  if (field.is_array) {
    formRules[field.name] = {
      requiredArray: requiredArray(isRequired),
      validNumberArray: validNumberArray(field.type),
    };
  } else {
    formRules[field.name] = {
      required: requiredIf(() => isRequired),
      validNumber: validNumber(field.type),
    };
  }
});

const castParameterValue = (value, type, isArray = false) => {
  if (isArray) {
    return value
      .map(v => castParameterValue(v, type))
      .filter(v => v !== null && v !== '');
  }

  if (type === 'integer' || type === 'float') {
    if (value === '' || value === null) return null;
    return type === 'integer' ? Math.round(value) : Number(value);
  }
  if (type === 'boolean') {
    return !!value;
  }
  return value === null || value === undefined ? null : `${value}`;
};

// Optional parameters left empty are omitted so the server-side schema validation
// sees the same payload a real MCP client would send.
const state = computed(() => {
  const data = {};
  props.parameterFields.forEach(field => {
    const value = castParameterValue(
      formState.value[field.name],
      field.type,
      field.is_array
    );
    if (value === null && !field.required) return;
    data[field.name] = value;
  });
  return data;
});

const v$ = useVuelidate(formRules, formState);

const validate = async () => {
  return v$.value.$validate();
};

const getFieldLabel = field => {
  return field.name + (field.required ? ' *' : '');
};

const addArrayItem = field => {
  const defaultValue = field.type === 'boolean' ? false : null;
  formState.value[field.name].push(defaultValue);
};

const removeArrayItem = (field, index) => {
  formState.value[field.name].splice(index, 1);
};

const updateArrayItem = (field, index, value) => {
  formState.value[field.name][index] = value;
};

defineExpose({ state, validate });
</script>

<template>
  <form class="space-y-4">
    <p v-if="!parameterFields.length" class="text-sm text-n-slate-11 py-4">
      {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.NO_PARAMETERS') }}
    </p>

    <div v-for="field of parameterFields" :key="field.name" class="form-field">
      <div v-if="field.is_array" class="space-y-2">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12 flex gap-1 items-center relative"
        >
          <span>{{ getFieldLabel(field) }}</span>
          <div
            v-if="field.description"
            v-tooltip.right="{ content: field.description, container: false }"
            class="flex items-center justify-center flex-shrink-0 rounded-full hover:bg-n-alpha-2 size-4"
          >
            <Icon
              icon="i-lucide-info"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
        </label>
        <div
          v-for="(item, index) in formState[field.name]"
          :key="index"
          class="flex gap-2 items-center"
        >
          <div class="flex-1">
            <TestToolFormField
              :model-value="item"
              :type="field.type"
              name=""
              description=""
              :required="false"
              :rules="{}"
              @update:model-value="v => updateArrayItem(field, index, v)"
            />
          </div>
          <Button
            size="xs"
            color="ruby"
            variant="ghost"
            icon="i-lucide-trash-2"
            class="mt-0.5"
            @click.prevent="removeArrayItem(field, index)"
          />
        </div>
        <Button
          size="sm"
          color="slate"
          variant="faded"
          icon="i-lucide-plus"
          :label="
            t(
              'WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.FORM.PARAMETERS.ADD_ITEM_LABEL'
            )
          "
          @click.prevent="addArrayItem(field)"
        />
        <span
          v-if="v$[field.name]?.$error"
          class="block min-w-0 mt-1 mb-0 text-xs truncate text-n-ruby-9 dark:text-n-ruby-9"
        >
          {{
            t(
              'WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.FORM.PARAMETERS.ERRORS.REQUIRED'
            )
          }}
        </span>
      </div>

      <TestToolFormField
        v-else
        v-model="formState[field.name]"
        :type="field.type"
        :name="field.name"
        :description="field.description"
        :required="field.required == true"
        :rules="formRules[field.name]"
      />
    </div>
  </form>
</template>
